import { MeasureName } from "./performanceProfiler/measureName";
import PerformanceProfilerFactory from "./performanceProfiler/performanceProfilerFactory";

/**
 * Defers the execution of a function using requestIdleCallback if available,
 * otherwise falls back to setTimeout with a minimal delay.
 *
 * @param callback The function to execute when the browser is idle or after a timeout
 * @param options Optional configuration for requestIdleCallback
 * @param isVerboseLoggingEnabled If true, logs performance profiling information
 * @returns A function to cancel the deferred execution
 */
export function deferUntilIdle(
    callback: () => void,
    options?: {
        timeout?: number;
        isVerboseLoggingEnabled?: boolean;
    }
): () => void {
    let cancelled = false;
    let id: number;

    const wrappedCallback = () => {
        if (!cancelled) {
            callback();
        }
    };

    // Use requestIdleCallback if available
    if (typeof requestIdleCallback === "function") {
        let deferUntilIdleProfile = PerformanceProfilerFactory.getInstance(MeasureName.DeferUntilIdle);
        if (options?.isVerboseLoggingEnabled) {
            deferUntilIdleProfile.start();
        }
        id = requestIdleCallback(() => {
            if (options?.isVerboseLoggingEnabled) {
                deferUntilIdleProfile.end();
            }
            wrappedCallback();
        }, options);
        return () => {
            cancelled = true;
            cancelIdleCallback(id);
        };
    }

    // Fallback to setTimeout with minimal delay
    id = setTimeout(wrappedCallback, 0);
    return () => {
        cancelled = true;
        clearTimeout(id);
    };
}
